import { useCallback, useState } from 'react';
import { useAtomValue } from 'jotai';
import { format } from 'date-fns';
import { tz } from '@date-fns/tz';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Dialog,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';

import { selectedDeviceSafeAtom } from '@/components/common/MapView';
import { subscriptionAtom } from '@/interfaces/subscription';
import { timeZoneAtom } from '@/interfaces/utils';
import ManageSubscriptionDialog from '@/components/devices/ManageSubscriptionDialog';

export default function DeviceDetailSubscription() {
  const { name, serialNumber, id } = useAtomValue(selectedDeviceSafeAtom);
  const { data: subscription, refetch } = useAtomValue(subscriptionAtom);
  const timeZone = useAtomValue(timeZoneAtom);

  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const timeFormat = i18n.language.startsWith('ko') ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';

  const onDialogClose = useCallback(
    (reason?: string) => {
      setOpen(false);
      if (reason === 'updated') {
        refetch();
      }
    },
    [refetch],
  );

  if (!subscription)
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
        <CircularProgress size={48} />
      </Stack>
    );
  return (
    <>
      <Stack gap={4} sx={{ flexGrow: 1, overflow: 'auto' }}>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>{t('Common.label.name')}:</TableCell>
                <TableCell>{name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('Devices.label.serialNumber')}:</TableCell>
                <TableCell>{serialNumber}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t('Devices.label.serviceType')}:</TableCell>
                <TableCell>{subscription.plan}</TableCell>
              </TableRow>
              {subscription.expiredAt && (
                <TableRow>
                  <TableCell>{t('Devices.label.subscriptionExpires')}:</TableCell>
                  <TableCell>{format(subscription.expiredAt, timeFormat, { in: tz(timeZone) })}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell>{t('Devices.label.remainingSMS')}:</TableCell>
                <TableCell>{subscription.smsCount}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Button variant="contained" onClick={() => setOpen(true)} sx={{ py: 1 }}>
          {t('Devices.label.manageSubscription')}
        </Button>
      </Stack>

      <Dialog open={open} onClose={onDialogClose} sx={{ '& .MuiPaper-root': { maxWidth: 'unset' } }}>
        <ManageSubscriptionDialog sensorId={id} planName={subscription?.plan} onClose={onDialogClose} />
      </Dialog>
    </>
  );
}
